import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/shared/lib/queryClient';
import { AuthProvider } from '@/auth/AuthContext';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RequireTile } from '@/auth/RequireTile';
import { RequireAppraisalsAccess } from '@/auth/RequireAppraisalsAccess';
import { RequireCoportalPage } from '@/auth/RequireCoportalPage';
import { Shell } from '@/shell/Shell';
import { ModuleMyNotesPage } from '@/shared/components/ModuleMyNotesPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ForbiddenPage } from '@/features/ForbiddenPage';
import { NotFoundPage } from '@/features/NotFoundPage';
import { LegacyHubRedirect } from '@/features/LegacyHubRedirect';
import { PublicTransferRoute } from '@/features/public-transfers/PublicTransferPage';
import { PublicPagesGate } from '@/features/PublicPagesGate';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { LiveAnnouncerProvider } from '@/shared/components/LiveAnnouncer';
import { VersionNotice } from '@/shared/components/VersionNotice';
import { NOTIFICATIONS_PATH } from '@/shell/notifications/paths';

// LoginPage is lazy so react-hook-form + zod (~98 kB) stay off the
// unauthenticated cold-load path for anonymous traffic that lands on
// public routes (e.g. /transfers/:token) or gets redirected to /login.
// VITE-CHUNKING-CRITICAL-PATH-REVIEW.
const LoginPage = lazy(() =>
  import('@/features/login/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/login/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/login/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
// Sign-in hand-off from Quotinc (Q-AMASSA-LINK). nginx forwards this path to
// the API in production; the page covers the dev server.
const QuotincSsoForward = lazy(() =>
  import('@/features/quotinc/QuotincSsoForward').then((m) => ({
    default: m.QuotincSsoForward,
  })),
);

// Accept an invite (2026-10-27): choose a password from the emailed link.
const SetPasswordPage = lazy(() =>
  import('@/features/login/SetPasswordPage').then((m) => ({
    default: m.SetPasswordPage,
  })),
);

// Module routes are lazy — mirrors Angular's loadChildren.
const AdminRoutes = lazy(() => import('@/features/admin/admin.routes'));
const NotificationsPage = lazy(() => import('@/shell/notifications/NotificationsPage'));
const CoportalRoutes = lazy(() => import('@/features/coportal/coportal.routes'));
const LemaxRoutes = lazy(() => import('@/features/lemax/lemax.routes'));
const RestoolsRoutes = lazy(() => import('@/features/restools/restools.routes'));
const GuestPortalRoutes = lazy(() => import('@/features/guest-portal/guestPortal.routes'));
const PreferencesRoutes = lazy(() => import('@/features/preferences/preferences.routes'));
const ContractingRoutes = lazy(() => import('@/features/contracting/contracting.routes'));
const ProductRoutes = lazy(() => import('@/features/product/product.routes'));
const AiaiaiRoutes = lazy(() => import('@/features/aiaiai/aiaiai.routes'));
const HrRoutes = lazy(() => import('@/features/hr/hr.routes'));
// Appraisals mounts separately from the admin-only HR subtree (broader
// audience: every employee sees their own form). See RequireAppraisalsAccess.
const AppraisalsRoutes = lazy(() => import('@/features/hr/appraisals/appraisals.routes'));
const ManagementRoutes = lazy(() => import('@/features/management/management.routes'));
const DataRoutes = lazy(() => import('@/features/data/data.routes'));
const ItRoutes = lazy(() => import('@/features/it/it.routes'));
const FinanceRoutes = lazy(() => import('@/features/finance/finance.routes'));
const EcommerceRoutes = lazy(() => import('@/features/ecommerce/ecommerce.routes'));
const BizdevRoutes = lazy(() => import('@/features/bizdev/bizdev.routes'));
// Team modules (Admin ▸ Modules ▸ Blank team module), at /m/<id>.
const TeamModuleRoutes = lazy(() => import('@/features/team-module/TeamModuleRoutes'));

// Employee Hub mounts OUTSIDE the Shell (full-bleed, its own bright identity).
const EmployeePortalRoutes = lazy(() => import('@/features/employeeportal/employeeportal.routes'));
// Agent Portal (external travel agents) — outside the Shell too, and lazy so
// none of it ships to staff who never open /agent.
const AgentPortalRoutes = lazy(() => import('@/features/agent-portal/AgentPortalRoutes'));

function ModuleFallback() {
  return (
    <div
      style={{
        fontFamily: 'var(--hm-mono)',
        fontSize: 11,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      loading module.
    </div>
  );
}

/**
 * Per-module guard — each lazy bundle gets its own ErrorBoundary so one
 * broken module (e.g. `/aiaiai` throwing on import) does not take down
 * its siblings. The outer Shell boundary still catches anything that
 * escapes (e.g. errors inside DashboardPage).
 */
function LazyModule({ scope, children }: { scope: string; children: ReactNode }) {
  return (
    <ErrorBoundary scope={scope}>
      <Suspense fallback={<ModuleFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/*
          BUG-QAr9r7-14/15 — LiveAnnouncerProvider used to mount inside
          <Shell>, which meant anonymous routes (/login, /transfers/:token)
          rendered outside any provider and every useLiveAnnounce() in
          public flows silently fell back to the no-op stub. Hoisted
          above the route switch so the live region is available to
          the entire tree. The region itself is `sr-only` so there's
          no visual cost; only one provider must exist, so the nested
          one inside Shell has been removed.
        */}
        <BrowserRouter>
          <LiveAnnouncerProvider>
            {/* Guest pages at the PHP URLs — /<ref>, /cs/<ref> (transfer page)
                and /guide, /places, /tours, /gifts, /gift-list. A gate, not
                routes: a `/:ref` route would outrank `*` for every one-segment
                path. See PublicPagesGate. */}
            <PublicPagesGate>
            <Routes>
            <Route
              path="/login"
              element={
                <LazyModule scope="login">
                  <LoginPage />
                </LazyModule>
              }
            />
            {/* Password reset — anonymous, no Shell chrome, same precedence
                as /login. The reset link mailed to the user points at
                /reset-password?token=… */}
            <Route
              path="/forgot-password"
              element={
                <LazyModule scope="login">
                  <ForgotPasswordPage />
                </LazyModule>
              }
            />
            <Route
              path="/reset-password"
              element={
                <LazyModule scope="login">
                  <ResetPasswordPage />
                </LazyModule>
              }
            />
            {/* Quotinc's "Amassa" link lands here with a one-minute token;
                it goes straight on to /api/auth/quotinc/sso. Anonymous, no
                Shell, same precedence as /login. */}
            <Route
              path="/sso/quotinc"
              element={
                <LazyModule scope="login">
                  <QuotincSsoForward />
                </LazyModule>
              }
            />
            {/* Invites: the link an admin's invite mails (single use, 7 days). */}
            <Route
              path="/set-password"
              element={
                <LazyModule scope="login">
                  <SetPasswordPage />
                </LazyModule>
              }
            />
            <Route path="/forbidden" element={<ForbiddenPage />} />

            {/*
              Agent Portal — external travel agents (PHP /agentportal). Its own
              sign-in and one read-only page; never the staff Shell. It gates
              itself (signed out → agent sign-in; agent → logs; staff → a
              pointer back), so no ProtectedRoute here. The server confines an
              agent token to this portal's endpoints regardless.
            */}
            <Route
              path="/agent/*"
              element={
                <LazyModule scope="agent-portal">
                  <AgentPortalRoutes />
                </LazyModule>
              }
            />
            {/* PHP URL, kept for old agent emails / bookmarks. nginx 301s it in
                production; this covers the dev server. */}
            <Route path="/agentportal/*" element={<Navigate to="/agent" replace />} />

            {/*
              Guest transfer page — anonymous, no auth, no Shell chrome. The
              PHP URLs (/<ref>, /cs/<ref>) are served by PublicPagesGate
              above; this is the older Amassa URL for the same page. See
              docs/cutover-public-transfers.md.
            */}
            <Route path="/transfers/:token" element={<PublicTransferRoute />} />

            {/*
              Legacy PHP Hub URLs (`/hub/<module>/?p=<page>&id=…`) from old
              emails and bookmarks — rewrite to the React route instead of
              404ing. Top-level so the redirect fires before auth: the
              TARGET route still runs ProtectedRoute/RequireTile.
            */}
            <Route path="/hub/*" element={<LegacyHubRedirect />} />

            {/*
              Employee Hub — authenticated, but rendered OUTSIDE the <Shell>
              chrome so it owns its whole canvas with its own bright identity.
              Same precedence trick as /login + /transfers (top-level route);
              still gated by ProtectedRoute (→ /login) + RequireTile (→ /forbidden).
            */}
            <Route
              path="/employeeportal/*"
              element={
                <ProtectedRoute>
                  <RequireTile tile="employee-hub">
                    <LazyModule scope="employeeportal">
                      <EmployeePortalRoutes />
                    </LazyModule>
                  </RequireTile>
                </ProtectedRoute>
              }
            />

            <Route
              element={
                <ProtectedRoute>
                  <ErrorBoundary scope="shell">
                    <Shell />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* Canonical home for the per-user notes surface, hoisted to
                  the top bar 2026-07-29. The per-module /<slug>/my-notes
                  routes still resolve (bookmarks, deep links) — they were
                  only removed from the nav rails, not from the routers. */}
              <Route path="/my-notes" element={<ModuleMyNotesPage />} />

              {/* The inbox behind the top bar's bell (2026-10-26): every
                  tenant, every staff user, no tile. A shell page rather than
                  the Employee Hub's (own tile, own layout): the notices come
                  from every module. /employeeportal/inbox leads here. */}
              <Route
                path={NOTIFICATIONS_PATH}
                element={
                  <Suspense fallback={null}>
                    <NotificationsPage />
                  </Suspense>
                }
              />

              <Route
                path="/admin/*"
                element={
                  <RequireTile tile="admin">
                    <LazyModule scope="admin">
                      <AdminRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              {/* One About page for every workspace (brief R14): the old CS/OPS
                  copy redirects to Preferences ▸ About, ahead of the module's
                  gates so anyone holding the old link lands on it. */}
              <Route path="/coportal/about" element={<Navigate to="/preferences/about" replace />} />
              {/*
                Two gates, not one. `RequireTile` answers "may this user open
                the CS/OPS Portal at all"; `RequireCoportalPage` answers "may
                they open THIS page of it" — the bare-slug half of the rights
                matrix, which had no client consumer at all until now.
              */}
              <Route
                path="/coportal/*"
                element={
                  <RequireTile tile="cs-portal">
                    <RequireCoportalPage>
                      <LazyModule scope="coportal">
                        <CoportalRoutes />
                      </LazyModule>
                    </RequireCoportalPage>
                  </RequireTile>
                }
              />
              <Route
                path="/guest-portal/*"
                element={
                  <RequireTile tile="guest-portal">
                    <LazyModule scope="guest-portal">
                      <GuestPortalRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/restools/*"
                element={
                  <RequireTile tile="res-tools">
                    <LazyModule scope="restools">
                      <RestoolsRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/lemax/*"
                element={
                  <RequireTile tile="lemax">
                    <LazyModule scope="lemax">
                      <LemaxRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/contracting/*"
                element={
                  <RequireTile tile="contracting">
                    <LazyModule scope="contracting">
                      <ContractingRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/product/*"
                element={
                  <RequireTile tile="product">
                    <LazyModule scope="product">
                      <ProductRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/aiaiai/*"
                element={
                  <RequireTile tile="ai-ai-ai">
                    <LazyModule scope="aiaiai">
                      <AiaiaiRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/preferences/*"
                element={
                  <RequireTile tile="preferences">
                    <LazyModule scope="preferences">
                      <PreferencesRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />

              <Route
                path="/bizdev/*"
                element={
                  <RequireTile tile="bizdev">
                    <LazyModule scope="bizdev">
                      <BizdevRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/data/*"
                element={
                  <RequireTile tile="data">
                    <LazyModule scope="data">
                      <DataRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/ecommerce/*"
                element={
                  <RequireTile tile="ecommerce">
                    <LazyModule scope="ecommerce">
                      <EcommerceRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/finance/*"
                element={
                  <RequireTile tile="finance">
                    <LazyModule scope="finance">
                      <FinanceRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              {/*
                Appraisals — broader audience than the rest of HR. Mounted
                BEFORE the admin-only `/hr/*` route so React Router matches the
                more-specific path first; managers/employees reach it via its
                own guard while /hr/cash-claims etc. stay admin-gated below.
              */}
              <Route
                path="/hr/appraisals/*"
                element={
                  <RequireAppraisalsAccess>
                    <LazyModule scope="hr">
                      <AppraisalsRoutes />
                    </LazyModule>
                  </RequireAppraisalsAccess>
                }
              />
              <Route
                path="/hr/*"
                element={
                  <RequireTile tile="hr">
                    <LazyModule scope="hr">
                      <HrRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/it/*"
                element={
                  <RequireTile tile="it">
                    <LazyModule scope="it">
                      <ItRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />
              <Route
                path="/management/*"
                element={
                  <RequireTile tile="management">
                    <LazyModule scope="management">
                      <ManagementRoutes />
                    </LazyModule>
                  </RequireTile>
                }
              />

              {/* Team modules a tenant added in Admin ▸ Modules (their own
                  gate: the module must exist, and the person be on staff). */}
              <Route
                path="/m/:slug/*"
                element={
                  <LazyModule scope="team-module">
                    <TeamModuleRoutes />
                  </LazyModule>
                }
              />

              {/* Authenticated 404 — typos under any Shell route land here
                  instead of silently bouncing to /dashboard. */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Unauth 404 — anonymous typos. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <VersionNotice />
            </PublicPagesGate>
          </LiveAnnouncerProvider>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
